import { User, UserManager } from 'oidc-client-ts';

import {
  AuthTokenResponse,
  UserSession,
  WorkspaceContext,
  WorkspaceMembership,
} from '../auth/types/authTypes';
import {
  clearCredentials,
  getRefreshCredential,
  getWorkspaceContext,
  getWorkspacePreference,
  setAccessCredential,
  setRefreshCredential,
  setWorkspaceContext,
} from '../auth/utils/inMemoryTokenStore';
import {
  extractWorkspaceClaimsFromToken,
  isWorkspaceScopedToken,
  mapTokenResponseToCredentials,
  parseJwtPayload,
} from '../auth/utils/tokenMapper';

import { oidcUserManagerSettings } from '@config/authConfig';

class OidcService {
  private readonly userManager: UserManager;

  constructor() {
    this.userManager = new UserManager(oidcUserManagerSettings);

    this.userManager.events.addAccessTokenExpiring(() => {
      this.refreshAccessToken('pre_expiry').catch(() => {
        // caller (Axios interceptor) handles reauth_required dispatch
      });
    });
  }

  async login(returnTo?: string): Promise<void> {
    await this.userManager.signinRedirect({
      state: { returnTo: returnTo ?? '/' },
    });
  }

  async handleCallback(): Promise<{ returnTo: string }> {
    const user = await this.userManager.signinRedirectCallback(
      window.location.href
    );

    const tokenResponse: AuthTokenResponse = {
      accessToken: user.access_token,
      refreshToken: user.refresh_token,
      tokenType: 'Bearer',
      expiresIn: user.expires_in ?? 3600,
      scope: user.scope,
      idToken: user.id_token,
    };

    const { access, refresh } = mapTokenResponseToCredentials(
      tokenResponse,
      new Date()
    );
    setAccessCredential(access);
    if (refresh) {
      setRefreshCredential(refresh);
    }

    const state = user.state as { returnTo?: string } | undefined;
    return { returnTo: state?.returnTo ?? '/' };
  }

  async restoreSession(): Promise<UserSession | null> {
    let user = await this.userManager.getUser();

    // No user in store (e.g. after page refresh) — try silent sign-in via SSO cookie
    if (!user) {
      try {
        user = await this.userManager.signinSilent();
      } catch {
        return null;
      }
    }

    if (!user) {
      return null;
    }

    const now = Math.floor(Date.now() / 1000);
    const isExpired = user.expires_at !== undefined && user.expires_at < now;

    if (isExpired) {
      try {
        await this.refreshAccessToken('pre_expiry');
        const refreshedUser = await this.userManager.getUser();
        if (!refreshedUser) return null;
        return this._buildUserSession(refreshedUser.access_token);
      } catch {
        return null;
      }
    }

    const tokenResponse: AuthTokenResponse = {
      accessToken: user.access_token,
      refreshToken: user.refresh_token,
      tokenType: 'Bearer',
      expiresIn: user.expires_in ?? 3600,
      scope: user.scope,
    };

    const { access, refresh } = mapTokenResponseToCredentials(
      tokenResponse,
      new Date()
    );
    setAccessCredential(access);
    if (refresh) {
      setRefreshCredential(refresh);
    }

    if (isWorkspaceScopedToken(user.access_token)) {
      const ctx = extractWorkspaceClaimsFromToken(user.access_token);
      if (
        ctx.tenantId &&
        ctx.membershipId &&
        ctx.workspaceSessionId &&
        ctx.contextType
      ) {
        // JWT claims never include display names — merge from the persisted
        // workspace preference so names survive page reloads.
        const pref = getWorkspacePreference();
        const isSameMembership = pref?.membershipId === ctx.membershipId;
        const workspaceContext: WorkspaceContext = {
          tenantId: ctx.tenantId,
          tenantName:
            ctx.tenantName ?? (isSameMembership ? pref?.tenantName : undefined),
          companyId: ctx.companyId,
          companyName:
            ctx.companyName ??
            (isSameMembership ? pref?.companyName : undefined),
          contextType: ctx.contextType,
          roles: ctx.roles ?? [],
          membershipId: ctx.membershipId,
          workspaceSessionId: ctx.workspaceSessionId,
        };
        setWorkspaceContext(workspaceContext);
      }
    } else {
      // Identity-only token. Re-upgrade silently if we have a persisted
      // workspace context (e.g. after a page refresh following signinSilent).
      const persistedCtx = getWorkspaceContext();
      if (persistedCtx) {
        try {
          await this.upgradeToWorkspaceToken({
            membershipId: persistedCtx.membershipId,
            tenantId: persistedCtx.tenantId,
            tenantName: persistedCtx.tenantName ?? '',
            companyId: persistedCtx.companyId,
            companyName: persistedCtx.companyName,
            contextType: persistedCtx.contextType,
            roles: persistedCtx.roles,
          });
        } catch {
          // Upgrade failed — clear stale context; user will re-select
          clearCredentials();
          return null;
        }
      }
    }

    return this._buildUserSession(user.access_token);
  }

  async upgradeToWorkspaceToken(
    membership: WorkspaceMembership
  ): Promise<WorkspaceContext> {
    const refreshCred = getRefreshCredential();
    if (!refreshCred || refreshCred.status !== 'active') {
      throw new Error(
        'No active refresh token available for workspace upgrade.'
      );
    }

    const authority = oidcUserManagerSettings.authority?.replace(/\/$/, '');
    const clientId = oidcUserManagerSettings.client_id;

    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: clientId ?? 'tuos-ui',
      refresh_token: refreshCred.value,
      tenant_id: membership.tenantId,
      workspace_context_type: membership.contextType,
    });

    if (membership.contextType === 'company' && membership.companyId) {
      params.set('company_id', membership.companyId);
    }

    const response = await fetch(`${authority}/connect/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: params.toString(),
    });

    if (!response.ok) {
      const errBody = (await response.json().catch(() => ({}))) as {
        error_description?: string;
      };
      throw new Error(
        errBody.error_description ?? `Token upgrade failed (${response.status})`
      );
    }

    const raw = (await response.json()) as {
      access_token: string;
      refresh_token?: string;
      token_type: string;
      expires_in: number;
      scope?: string;
    };

    const tokenResponse: AuthTokenResponse = {
      accessToken: raw.access_token,
      refreshToken: raw.refresh_token,
      tokenType: 'Bearer',
      expiresIn: raw.expires_in,
      scope: raw.scope,
    };

    const issuedAt = new Date();
    const { access, refresh } = mapTokenResponseToCredentials(
      tokenResponse,
      issuedAt
    );

    // Mark the used refresh token before rotating
    refreshCred.status = 'used';

    setAccessCredential(access);
    if (refresh) {
      setRefreshCredential(refresh);
    }

    const jwtClaims = extractWorkspaceClaimsFromToken(raw.access_token);

    const workspaceContext: WorkspaceContext = {
      tenantId: membership.tenantId,
      tenantName: membership.tenantName,
      companyId: membership.companyId,
      companyName: membership.companyName,
      contextType: membership.contextType,
      roles: jwtClaims.roles?.length ? jwtClaims.roles : membership.roles,
      membershipId: jwtClaims.membershipId ?? '',
      workspaceSessionId: jwtClaims.workspaceSessionId ?? '',
    };

    setWorkspaceContext(workspaceContext);

    // Persist the upgraded token back into the UserManager store so it
    // survives a page refresh and restoreSession() can read it.
    try {
      const currentUser = await this.userManager.getUser();
      if (currentUser) {
        const upgradedUser = new User({
          ...currentUser,
          access_token: raw.access_token,
          refresh_token: raw.refresh_token ?? currentUser.refresh_token,
          expires_at: Math.floor(Date.now() / 1000) + raw.expires_in,
          scope: raw.scope ?? currentUser.scope,
        });
        await this.userManager.storeUser(upgradedUser);
      }
    } catch {
      // Non-critical — session still works for the current tab
    }

    return workspaceContext;
  }

  async refreshAccessToken(
    reason: 'pre_expiry' | 'unauthorized_response' | 'company_switch'
  ): Promise<void> {
    const ctx = getWorkspaceContext();

    const extraTokenParams: Record<string, string> = {};
    if (ctx) {
      extraTokenParams['tenant_id'] = ctx.tenantId;
      extraTokenParams['workspace_context_type'] = ctx.contextType;
      if (ctx.companyId) {
        extraTokenParams['company_id'] = ctx.companyId;
      }
    }

    const user = await this.userManager.signinSilent({ extraTokenParams });
    if (!user) {
      throw new Error(`Silent renewal failed (reason: ${reason})`);
    }

    const tokenResponse: AuthTokenResponse = {
      accessToken: user.access_token,
      refreshToken: user.refresh_token,
      tokenType: 'Bearer',
      expiresIn: user.expires_in ?? 3600,
      scope: user.scope,
    };

    const { access, refresh } = mapTokenResponseToCredentials(
      tokenResponse,
      new Date()
    );
    setAccessCredential(access);
    if (refresh) {
      setRefreshCredential(refresh);
    }
  }

  async logout(): Promise<void> {
    clearCredentials();
    await this.userManager.signoutRedirect();
  }

  private _buildUserSession(accessToken: string): UserSession {
    let subjectId = '';
    let displayName: string | undefined;

    try {
      const payload = parseJwtPayload(accessToken);
      subjectId = (payload['sub'] as string) ?? '';
      const name = payload['name'] as string | undefined;
      const givenName = payload['given_name'] as string | undefined;
      const familyName = payload['family_name'] as string | undefined;
      const email = payload['email'] as string | undefined;
      displayName =
        name ??
        (givenName && familyName ? `${givenName} ${familyName}` : undefined) ??
        email;
    } catch {
      // non-critical
    }

    return {
      sessionId: crypto.randomUUID(),
      subjectId,
      displayName,
      status: 'authenticated',
      workspaceContext: getWorkspaceContext() ?? undefined,
      createdAt: new Date().toISOString(),
      lastAuthenticatedAt: new Date().toISOString(),
    };
  }
}

export const oidcService = new OidcService();
